import { Box, MenuList, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  Building2 as BusinessIcon,
  Users as GroupIcon,
  Handshake as LoanPackagesIcon,
  LogOut as LogoutIcon,
  FileText as NoteIcon,
  User as PersonIcon,
  Settings as SettingsIcon,
  Webhook as WebhookIcon,
} from "lucide-react";
import { useHistory } from "react-router-dom";

import { UserTypeRoleType } from "api/user/types";
import { hasPermissions, hasRole } from "components/stores/UserStore";
import { PermissionsType } from "components/stores/UserStore/permissions";

import SidebarMenuItem, { SidebarMenuItemsProps } from "./SidebarMenuItem";

export type SidebarMenuListChild = Array<
  SidebarMenuItemsProps & {
    requiredPermissions?: PermissionsType[];
    requiredRole?: UserTypeRoleType[];
  }
>;

type SidebarMenuList = Array<{
  id: string;
  text: string | JSX.Element;
  list: SidebarMenuListChild;
}>;

const sidebarMenuListNav: SidebarMenuList = [
  {
    id: "main",
    text: "Application Dashboard",
    list: [
      {
        icon: <NoteIcon />,
        text: "Credit Evaluations",
        link: "/credit-evaluations",
        requiredPermissions: ["read:credit-evaluations"],
      },
      {
        icon: <LoanPackagesIcon />,
        text: "Loan Packages",
        link: "/loan-packages",
        requiredPermissions: ["read:loan-packages"],
      },
      {
        icon: <GroupIcon />,
        text: "Customers",
        link: "/customers",
        requiredPermissions: ["read:customers"],
      },
      {
        icon: <PersonIcon />,
        text: "User Management",
        link: "/users",
        requiredPermissions: ["read:users"],
      },
      {
        icon: <BusinessIcon />,
        text: "Organisations",
        link: "/organisations",
        requiredPermissions: ["read:organisations"],
      },
    ],
  },
];

const SidebarMenu = ({
  onClose,
  collapsed,
}: {
  onClose?: () => void;
  collapsed?: boolean;
}) => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const handleLogout = () => {
    localStorage.removeItem("JWToken");
    queryClient.removeQueries([]);
    history.push("/login");
    onClose?.();
  };

  const utilityItems: SidebarMenuItemsProps[] = [
    {
      icon: <SettingsIcon />,
      text: "User profile",
      link: "/user/profile",
    },
    ...(hasPermissions(["read:logs"])
      ? [
          {
            icon: <WebhookIcon />,
            text: "Webhook Logs",
            link: "/webhook-logs",
          },
        ]
      : []),
    {
      icon: <LogoutIcon />,
      text: "Logout",
      onClick: handleLogout,
    },
  ];

  return (
    <Stack height="100%" flex={1} justifyContent="space-between">
      <MenuList
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          pt: 0.5,
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {sidebarMenuListNav.map(({ id, list }) => {
          const filtered = list.filter(
            (item) =>
              hasPermissions(item.requiredPermissions) &&
              hasRole(item.requiredRole),
          );
          if (!filtered.length) return null;
          return (
            <Box key={id} pt={1}>
              {filtered.map(({ icon, text, link, onClick }) => (
                <SidebarMenuItem
                  key={link ?? (typeof text === "string" ? text : "logout")}
                  icon={icon}
                  text={text}
                  link={link}
                  onClick={onClick ?? onClose}
                  collapsed={collapsed}
                />
              ))}
            </Box>
          );
        })}
      </MenuList>
      <MenuList sx={{ "&::-webkit-scrollbar": { display: "none" } }}>
        {utilityItems.map((item) => (
          <SidebarMenuItem
            key={item.link ?? "logout"}
            icon={item.icon}
            text={item.text}
            link={item.link}
            onClick={item.onClick ?? onClose}
            collapsed={collapsed}
          />
        ))}
      </MenuList>
    </Stack>
  );
};

export default SidebarMenu;
